import React, { useState, useEffect } from 'react';
import LocaleGlobe from './LocaleGlobe';
import { useTranslation } from '../../i18n/useTranslation';
import { useTheme } from '../../themes/ThemeProvider';

const LocalitySelector = ({ areas = [], onSelectArea, onChangeLocation, onManageLocations, currentUser, isGuest = false }) => {
  const { t } = useTranslation('location');
  const theme = useTheme();
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [globeLongitude, setGlobeLongitude] = useState(-9.1); // Silver Coast default

  // Calculate positions for 10 buttons around a circle
  const buttonPositions = Array.from({ length: 10 }, (_, i) => {
    const angle = (i * 36 - 90) * (Math.PI / 180); // Start at top, 36° between each
    const radius = 92.5; // Distance from center - 55px buttons edge 5px from outer edge
    return {
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius,
    };
  });

  // Initialize longitude for the globe
  useEffect(() => {
    async function initLongitude() {
      // Try to get from user's home location (if available)
      if (currentUser?.id) {
        try {
          // This would call the get_user_home_centroid RPC when available
          // For now, use IP geolocation fallback
          const lon = await getLongitudeFromIP();
          setGlobeLongitude(lon);
        } catch (error) {
          console.error('Failed to get longitude:', error);
        }
      } else {
        // Use IP geolocation for non-logged-in users
        const lon = await getLongitudeFromIP();
        setGlobeLongitude(lon);
      }
    }
    initLongitude();
  }, [currentUser]);

  // IP geolocation fallback using ip-api.com (free, no key needed)
  async function getLongitudeFromIP() {
    try {
      const res = await fetch('http://ip-api.com/json/?fields=lon');
      const data = await res.json();
      return data.lon || -9.1;
    } catch {
      return -9.1; // Silver Coast default
    }
  }

  const handleAreaClick = (area, index) => {
    setSelectedIndex(index);
    if (onSelectArea) {
      onSelectArea(area);
    }
  };

  const handleChangeLocationClick = () => {
    if (onChangeLocation) {
      onChangeLocation();
    }
  };

  const handleManageLocationsClick = () => {
    if (onManageLocations) {
      onManageLocations();
    }
  };

  // ── Guest reduced mode: centre button only, no dial/slots ──
  if (isGuest) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', paddingTop: '7px', pointerEvents: 'auto' }}>
        <button
          onClick={handleChangeLocationClick}
          style={{
            width: '120px',
            height: '120px',
            borderRadius: '9999px',
            background: '#024B99',
            color: 'white',
            fontWeight: '600',
            fontSize: '14px',
            borderWidth: '3px',
            borderStyle: 'solid',
            borderColor: '#DFE3F3',
            boxSizing: 'border-box',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            cursor: 'pointer',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            lineHeight: '1.3'
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#023870'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#024B99'; }}
        >
          {t('dial_guest_choose')}
        </button>
      </div>
    );
  }

  // ── Logged-in user: full dial ──
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', paddingTop: '7px', pointerEvents: 'auto' }}>
      {/* Header */}
      <div style={{ height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white', borderRadius: '9999px', padding: '0 32px', borderWidth: '3px', borderStyle: 'solid', borderColor: '#DFE3F3', boxSizing: 'border-box' }}>
        <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#024B99' }}>{t('dial_title')}</span>
      </div>

      {/* Dial Container */}
      <div style={{ position: 'relative', width: '250px', height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {/* Outer Circle - Background */}
        <div
          style={{
            position: 'absolute',
            width: '250px',
            height: '250px',
            borderRadius: '9999px',
            background: '#C8D2D7',
            borderWidth: '3px',
            borderStyle: 'solid',
            borderColor: '#b5b7c2ff',
            boxSizing: 'border-box',
          }}
        />

        {/* Area Buttons - Display area codes */}
        {buttonPositions.map((pos, index) => {
          const area = areas[index];
          const isActive = selectedIndex === index;

          const bgColor = area?.color_hex
            ? area.color_hex
            : area
              ? (isActive ? '#024B99' : 'white')
              : '#f3f4f6';

          const textColor = area
            ? (isActive ? 'white' : '#1a1a1a')
            : '#9ca3af';

          return (
            <button
              key={index}
              onClick={() => area && handleAreaClick(area, index)}
              disabled={!area}
              style={{
                position: 'absolute',
                width: '55px',
                height: '55px',
                borderRadius: '9999px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                left: '50%',
                top: '50%',
                transform: `translate(calc(-50% + ${pos.x}px), calc(-50% + ${pos.y}px))`,
                fontSize: '11px',
                fontWeight: '500',
                textAlign: 'center',
                boxSizing: 'border-box',
                WebkitAppearance: 'none',
                appearance: 'none',
                borderWidth: '3px',
                borderStyle: 'solid',
                borderColor: '#b5b7c2ff',
                outline: 'none',
                cursor: area ? 'pointer' : 'not-allowed',
                background: bgColor,
                color: textColor,
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                transition: 'all 0.2s',
                transform: `translate(calc(-50% + ${pos.x}px), calc(-50% + ${pos.y}px)) ${isActive ? 'scale(1.05)' : 'scale(1)'}`,
              }}
            >
              <span style={{ padding: '0 4px', lineHeight: '1.2', wordBreak: 'break-word' }}>
                {area?.area_code || t('dial_empty_slot', { index: index + 1 })}
              </span>
            </button>
          );
        })}

        {/* Center Button - split top (Change Location, enters a temporary visit) / bottom
            (Manage Locations, unchanged) — Change 3, addendum-1 §D. The outer circle stays a
            single visual unit (shared border/shadow); overflow:hidden clips the two stacked
            regions to the circle instead of individual button border-radius. */}
        <div
          style={{
            position: 'relative',
            width: '100px',
            height: '100px',
            borderRadius: '9999px',
            overflow: 'hidden',
            borderWidth: '3px',
            borderStyle: 'solid',
            borderColor: '#DFE3F3',
            boxSizing: 'border-box',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            zIndex: 10,
          }}
        >
          {/* Top 55% — Change Location (enters a visit) */}
          <button
            onClick={handleChangeLocationClick}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '55%',
              border: 'none',
              background: theme?.primary || '#024B99',
              color: 'white',
              fontWeight: '500',
              fontSize: '13px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: '4px',
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#023870'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = theme?.primary || '#024B99'; }}
          >
            {t('dial_guest_choose')}
          </button>

          {/* Bottom 45% — Manage Locations (unchanged). Divider reuses the same #DFE3F3 literal
              as the circle's own surround/ring border above (addendum-4 §1 — "same white as the
              surround"), not a new colour. */}
          <button
            onClick={handleManageLocationsClick}
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '45%',
              border: 'none',
              borderTop: '2px solid #DFE3F3',
              background: '#023870',
              color: 'white',
              fontWeight: '500',
              fontSize: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: '4px',
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#012c5c'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#023870'; }}
          >
            {t('dial_manage_button')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocalitySelector;
